import type { PathWatcherEvent } from '@webcontainer/api';
import type { AnalyzerWorker } from './analyzer';
import type { ModuleDeclaration } from './esm';
import { Resolver, isScript } from './resolver';
import { hashBuffer } from '~/utils/hash';
import { isBinaryFile } from '~/utils/is-binary-file';
import { withResolvers } from '~/utils/promises';
import { addWorkDir } from '~/utils/workDir';

const decoder = new TextDecoder();

export class ModuleGraph {
  resolver = new Resolver();
  #queuePromise = Promise.resolve();
  #queue: [string, string][] = [];
  #processing = false;
  #hashes: Record<string, string> = {};
  #modules: Record<string, ModuleDeclaration[]> = {};
  #dependencies: Map<string, Set<string>> = new Map();
  #dependents: Map<string, Set<string>> = new Map();

  constructor(public analyzer: AnalyzerWorker) {
    this.analyzer = analyzer;
  }

  async handleChanges(changes: PathWatcherEvent[]) {
    for (const change of changes) {
      if (isScript(change.path)) {
        if (change.type === 'add_file') {
          this.resolver.addPath(change.path);
        } else if (change.type === 'remove_file') {
          this.resolver.removePath(change.path);
          this.removeNode(change.path);
        }

        if (change.buffer && !isBinaryFile(change.buffer) && (change.type === 'add_file' || change.type === 'change')) {
          const hash = await hashBuffer(change.buffer);

          if (this.#hashes[change.path] !== hash) {
            this.#hashes[change.path] = hash;

            this.update(change.path, decoder.decode(change.buffer));
          }
        }
      }
    }
  }

  update(path: string, contents: string) {
    this.#queue.push([path, contents]);

    this.processQueue();
  }

  async processQueue() {
    if (this.#processing) {
      return;
    }

    this.#processing = true;

    const { promise, resolve } = withResolvers<void>();
    this.#queuePromise = promise;

    while (this.#queue.length > 0) {
      const [path, contents] = this.#queue.shift()!;

      const modules = await this.analyzer.extractModuleDeclarations(contents);

      this.#modules[path] = modules;
    }

    this.generateDependencies();
    this.#processing = false;
    resolve();
  }

  generateDependencies() {
    this.#dependents.clear();
    this.#dependencies.clear();

    Object.entries(this.#modules).forEach(([path, modules]) => {
      modules.forEach((decl) => {
        if (decl.type === 'import') {
          const target = this.resolver.resolve(path, decl.module.path);

          if (target) {
            this.addDependency(path, target);
          }
        }
      });
    });
  }

  addDependency(from: string, to: string) {
    if (!this.#dependencies.has(from)) {
      this.#dependencies.set(from, new Set());
    }

    if (!this.#dependents.has(to)) {
      this.#dependents.set(to, new Set());
    }

    this.#dependencies.get(from)!.add(to);
    this.#dependents.get(to)!.add(from);
  }

  removeNode(path: string) {
    delete this.#modules[path];
    delete this.#hashes[path];
    this.removeDependencies(path);
  }

  removeDependencies(path: string) {
    const outgoing = this.#dependencies.get(path) ?? [];

    for (const target of outgoing) {
      this.#dependents.get(target)?.delete(path);
    }

    const incoming = this.#dependents.get(path) ?? [];

    for (const source of incoming) {
      this.#dependencies.get(source)?.delete(path);
    }

    this.#dependencies.delete(path);
    this.#dependents.delete(path);
  }

  async wait() {
    await this.#queuePromise;
  }

  getModuleDeclarations(path: string) {
    return this.#modules[addWorkDir(path)];
  }

  getDependencies(path: string) {
    return this.#dependencies.get(addWorkDir(path)) ?? new Set();
  }

  getDependents(path: string) {
    return this.#dependents.get(addWorkDir(path)) ?? new Set();
  }
}
